import { useState } from 'react';
import useSWR from 'swr';

import { Pack } from '@/components/app/cards/PacksList/types';

import { useApi } from '../api';

export interface SearchResponse {
	pack: Pack;
	query_id: string;
	search_index: string;
}

enum SearchFilterType {
	CATEGORY = 'category',
	DURATION = 'duration',
}
export interface Filter {
	id: SearchFilterType;
	multi_select: boolean;
	title: string;
	options: { id: string; title: string }[];
}
export interface SearchCategoriesResponse {
	filters: {
		id: SearchFilterType;
		multi_select: boolean;
		title: string;
		options: { id: string; title: string }[];
	}[];
}

export interface SearchTrendingResponse {
	terms: {
		term: string;
	}[];
}

export default function useSearch(query?: string, category?: string[], duration?: string[]) {
	const apiRequest = useApi();
	const baseEndpoint = 'search/v2';

	const [usedFallback, setUseFallback] = useState(false);

	const getSearchEndpoint = (query?: string, category?: string[], duration?: string[]) => {
		return `${baseEndpoint}?query=${query}&category=${category}&duration=${duration}`;
	};

	const fetchData = async () => {
		const response = await apiRequest<SearchResponse>({
			endpoint: getSearchEndpoint(query, category, duration),
			method: 'GET',
		});
		if (response.data?.pack?.items?.length) {
			setUseFallback(false);
			return { ...response.data };
		}

		const fallbackResponse = await apiRequest<SearchResponse>({
			endpoint: getSearchEndpoint(query),
			method: 'GET',
		});
		setUseFallback(true);
		return { ...fallbackResponse.data };
	};

	const { data, isLoading, error } = useSWR<SearchResponse>(
		[baseEndpoint, query, category, duration].filter(Boolean),
		fetchData,
		{ revalidateOnFocus: false, errorRetryCount: 0, keepPreviousData: true },
	);

	return {
		isLoading: isLoading,
		pack: data?.pack,
		query_id: data?.query_id,
		search_index: data?.search_index,
		error,
		usedFallback,
	};
}

export function useSearchCategories() {
	const apiRequest = useApi();
	const endpoint = 'search/v2/config';
	const { data, isLoading, error } = useSWR<SearchCategoriesResponse>(
		endpoint,
		async () => {
			const res = await apiRequest<SearchCategoriesResponse>({
				endpoint: endpoint,
				method: 'GET',
			});
			return res.data;
		},
		{ revalidateOnFocus: true, errorRetryCount: 0, keepPreviousData: true },
	);
	return { isLoading: isLoading, filters: data?.filters, error };
}

export function useSearchTrending() {
	const apiRequest = useApi();
	const baseEndpoint = 'search/trending';

	const { data, isLoading, error } = useSWR<SearchTrendingResponse>(
		baseEndpoint,
		async () => {
			const res = await apiRequest<SearchTrendingResponse>({
				endpoint: `${baseEndpoint}`,
				method: 'GET',
			});
			return res.data;
		},
		{ revalidateOnFocus: false, errorRetryCount: 0, keepPreviousData: true },
	);

	return { isLoading, terms: data?.terms, error };
}

export interface SearchRecsResponse {
	input: string;
	terms: { term: string }[];
}

export function useSearchRecs(input?: string, limit?: number) {
	const apiRequest = useApi();
	const baseEndpoint = 'search/recommend';
	const { data, error, isLoading } = useSWR<SearchRecsResponse>(
		input ? [baseEndpoint, input] : [baseEndpoint],

		async () => {
			const res = await apiRequest<SearchRecsResponse>({
				endpoint: baseEndpoint,
				method: 'POST',
				body: { query: input, limit },
			});
			return res.data;
		},
		{ revalidateOnFocus: false, errorRetryCount: 0, keepPreviousData: true },
	);

	return { searchRecs: data?.terms, error, isLoading };
}
